import { ListaDeDados, type Dado } from '../../../../components/ListaDeDados'
import { dataHora } from '../../../../lib/datas'
import { ROTULO_CRITICIDADE_TIS, TERMOS, TEXTOS } from '../../../../lib/glossario'
import type { MarcacaoNaTela } from '../../../../types/chamado'

/**
 * O que a fiscalização marcou.
 *
 * A classificação do fiscal aparece ao lado da categoria do e-Niterói, e não no lugar dela. O TR
 * (4.1.2.3.4, p.16) autoriza a CONTRATANTE a alterar o serviço cadastrado depois do diagnóstico, e
 * o fiscal usou isso — "chamado fechado por estar com a categoria errada", anotado 8 vezes na
 * planilha de maio. Guardar só uma das duas apagaria a divergência, que é justamente o achado.
 */
export function Julgamento({
  marcacao,
  categoriaDoENiteroi,
}: {
  marcacao: MarcacaoNaTela
  categoriaDoENiteroi: string | null
}) {
  // Tirado da apuração, as respostas das quatro perguntas não existem — mostrar "Não" em cada uma
  // afirmaria um julgamento que ninguém fez. Fica o fato da exclusão, e o motivo logo abaixo.
  const campos: Dado[] = marcacao.contaNoMes ? camposDoJulgamento(marcacao, categoriaDoENiteroi) : [
    { rotulo: 'Categoria no e-Niterói', valor: categoriaDoENiteroi },
    { rotulo: 'Conta no mês', valor: TEXTOS.marcacao.foraDaApuracao.leitura },
    { rotulo: 'Registrado por', valor: quemRegistrou(marcacao) },
  ]

  return (
    <div className="space-y-5">
      <ListaDeDados dados={campos} />

      {marcacao.observacao && (
        <div>
          <h3 className="text-sm font-semibold text-slate-800">Observação do fiscal</h3>
          <p className="mt-1 max-w-prose whitespace-pre-line text-sm text-slate-700">{marcacao.observacao}</p>
        </div>
      )}

      {/* A decisão nº 1 (22/08/2026) fixou de onde vem a criticidade, e o TIS passou a ser
          apurado no fechamento. A nota antiga dizia que ele "não é calculado por este sistema" —
          enquanto o cartão do TIS, na mesma sessão, aparecia apurado incidente a incidente. */}
      <p className="max-w-prose rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
        <strong className="font-semibold">{TEXTOS.notaDoTis.titulo}</strong> {TEXTOS.notaDoTis.ajuda}
      </p>
    </div>
  )
}

/**
 * O que a tela diz quando ninguém julgou este chamado.
 *
 * "Sem análise" não é o mesmo que "sem problema": chamado sem julgamento fica fora de todos os
 * denominadores do mês. São 25 das 70 linhas da planilha de maio, todas com a observação "chamado
 * fechado em junho/2026" — e a diferença entre ficar de fora e estar em dia é a diferença entre um
 * denominador de 45 e um de 70.
 */
function camposDoJulgamento(marcacao: MarcacaoNaTela, categoriaDoENiteroi: string | null): Dado[] {
  return [
    { rotulo: 'Categoria no e-Niterói', valor: categoriaDoENiteroi },
    { rotulo: 'Classificação da fiscalização', valor: marcacao.classificacaoServico },
    { rotulo: 'Conta no mês', valor: sim(marcacao.contaNoMes) },
    { rotulo: `${TERMOS.ica.sigla} — entrega com inconformidade`, ajuda: 'ica', valor: sim(marcacao.icaInconformidade) },
    { rotulo: `${TERMOS.iap.sigla} — entra na conta de prazos`, ajuda: 'iap', valor: sim(marcacao.iapElegivel) },
    {
      rotulo: `${TERMOS.iap.sigla} — atendido no prazo`,
      valor: marcacao.iapNaoSeAplica ? TEXTOS.marcacao.prazo.naoSeAplica : sim(marcacao.iapNoPrazo),
    },
    { rotulo: `${TERMOS.iae.sigla} — aceite integral`, ajuda: 'iae', valor: aceiteIntegral(marcacao) },
    { rotulo: `${TERMOS.iae.sigla} — aceite parcial`, valor: sim(marcacao.iaeAceiteParcial) },
    { rotulo: 'É manutenção corretiva', valor: sim(marcacao.tisElegivel) },
    { rotulo: 'Criticidade marcada pelo fiscal', valor: criticidade(marcacao) },
    { rotulo: 'Registrado por', valor: quemRegistrou(marcacao) },
    { rotulo: 'Apoio da IA neste registro', valor: apoioDaIa(marcacao) },
  ]
}

export function SemJulgamento() {
  return (
    <div className="rounded-xl border border-dashed border-slate-300 px-4 py-5">
      <h3 className="text-sm font-semibold text-slate-900">{TEXTOS.chamados.julgamentoAusente.titulo}</h3>
      <p className="mt-1 max-w-prose text-sm text-slate-600">{TEXTOS.chamados.julgamentoAusente.ajuda}</p>
    </div>
  )
}

/**
 * De quanto apoio da IA este julgamento se valeu.
 *
 * Não é enfeite: é a resposta à pergunta que o contrato vai fazer — o fiscal julgou, ou assinou o
 * que a máquina escreveu? Sem esta linha a trilha da V20 seria gravada e nunca lida, e trilha que
 * ninguém lê não protege ninguém.
 */
function apoioDaIa(marcacao: MarcacaoNaTela) {
  const t = TEXTOS.marcacao.sugestaoDaIa
  if (!marcacao.parecerIaId) {
    return <span className="text-slate-500">{t.semIa}</span>
  }
  if (marcacao.camposAceitosDaIa.length === 0) {
    return t.trilhaNenhuma
  }
  const nomes = marcacao.camposAceitosDaIa.map((campo) => ROTULO_DO_CAMPO[campo] ?? campo)
  return `${t.trilha} ${nomes.join(', ')}.`
}

/** O nome de cada campo como a pergunta o chama — o mesmo vocabulário do formulário. */
const ROTULO_DO_CAMPO: Record<string, string> = {
  natureza: TEXTOS.marcacao.natureza.rotulo,
  prazo: TEXTOS.marcacao.prazo.rotulo,
  criticidade: TEXTOS.marcacao.criticidade.rotulo,
  conformidade: TEXTOS.marcacao.conformidade.rotulo,
  aceite: TEXTOS.marcacao.aceite.rotulo,
}

/** "Sim"/"Não" por extenso: um `✓` sozinho não diz se a ausência é "não" ou "não preenchido". */
function aceiteIntegral(marcacao: MarcacaoNaTela): string {
  if (marcacao.iaeNaoSeAplica) return TEXTOS.marcacao.aceite.naoSeAplica
  if (marcacao.iaeAceiteAposTentativas) return TEXTOS.marcacao.aceite.aposTentativas
  return sim(marcacao.iaeAceiteIntegral)
}

function sim(valor: boolean): string {
  return valor ? 'Sim' : 'Não'
}

function criticidade(marcacao: MarcacaoNaTela) {
  if (!marcacao.criticidadeTis) {
    return <span className="text-slate-500">Não classificada</span>
  }
  return ROTULO_CRITICIDADE_TIS[marcacao.criticidadeTis] ?? marcacao.criticidadeTisRotulo ?? marcacao.criticidadeTis
}

/**
 * Quem marcou e por qual caminho.
 *
 * A carga da planilha e a edição na tela são atos diferentes, e precisam ser distinguíveis meses
 * depois: um valor que veio de arquivo tem o arquivo por trás; um valor digitado tem uma pessoa.
 */
function quemRegistrou(marcacao: MarcacaoNaTela) {
  const via = marcacao.registradoVia === 'PLANILHA' ? 'pela carga da planilha' : 'na tela'
  return `${marcacao.registradoPorNome ?? 'Não informado'} · ${via} · ${dataHora(marcacao.registradoEm)}`
}
